import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

export default function SearchHistory({ onApplySearch }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: searchHistory = [] } = useQuery({
    queryKey: ['search-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('search_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data || []
    },
    enabled: !!user,
  })

  const { data: suggestions = [] } = useQuery({
    queryKey: ['search-suggestions'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_search_suggestions', {
        p_query: null,
        p_limit: 5,
      })

      if (error) throw error
      return data || []
    },
    enabled: !!user,
  })

  const clearHistoryMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('search_history')
        .delete()
        .eq('user_id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['search-history'])
    },
  })

  const handleApplySearch = (query, filters) => {
    if (onApplySearch) {
      onApplySearch(query, filters)
    }
  }

  if (!user) return null

  return (
    <div className="space-y-6">
      {/* Popular Searches */}
      {suggestions.length > 0 && (
        <div>
          <h4 className="font-medium text-secondary-900 mb-3">Popular Searches</h4>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleApplySearch(suggestion.query, {})}
                className="px-3 py-1 text-sm bg-primary-50 text-primary-700 rounded-full hover:bg-primary-100 transition-colors"
              >
                {suggestion.query}
                <span className="ml-2 text-xs text-primary-500">
                  ({suggestion.search_count})
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Searches */}
      {searchHistory.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-secondary-900">Recent Searches</h4>
            <button
              onClick={() => {
                if (confirm('Clear all search history?')) {
                  clearHistoryMutation.mutate()
                }
              }}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Clear
            </button>
          </div>
          <div className="space-y-2">
            {searchHistory.map((search) => (
              <button
                key={search.id}
                onClick={() => handleApplySearch(search.query, search.filters || {})}
                className="w-full text-left px-3 py-2 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-secondary-900">{search.query || 'Filtered search'}</span>
                  </div>
                  <span className="text-xs text-secondary-500">
                    {search.results_count} results
                  </span>
                </div>
                {search.filters && Object.keys(search.filters).length > 0 && (
                  <div className="text-xs text-secondary-600 mt-1 ml-6">
                    {Object.keys(search.filters).length} filters applied
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {searchHistory.length === 0 && suggestions.length === 0 && (
        <p className="text-sm text-secondary-500 text-center py-4">
          No search history yet
        </p>
      )}
    </div>
  )
}
